import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, EllipsisHorizontalIcon, ArrowDownTrayIcon } from "@heroicons/react/20/solid"
import { classNames } from "../../utils/styling-utils"

export interface IEnkButtonProps {
  text?: string
  type?: "button" | "submit" | "reset"
  fullWidth?: boolean
  onClick?: () => void
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

export interface IEnkMenuButtonProps extends IEnkButtonProps {
  items: string[]
  onItemSelected: (item: string) => void
}

type EnkMenuItems = string[]

export interface IEnkMoreMenuButtonProps {
  items: string[] | EnkMenuItems[]
  label?: string
  onItemSelected: (item: string) => void
}

export interface IEnkNavButtonsProps {
  onPrev: () => void
  onMiddle: () => void
  onNext: () => void
  kind: string
  middleText: string
}

export function EnkButton(props: IEnkButtonProps) {
  return (
    <button
      type={props.type || "button"}
      className={classNames(
        props.fullWidth ? "flex w-full items-center justify-center leading-6" : "ml-6 flex items-center gap-x-2",
        "rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
      )}
      onClick={props.onClick}
    >
      {props.icon && <props.icon id="photo" aria-hidden="true" className="h-4 w-4 text-gray-600" />}
      {props.text}
    </button>
  )
}

export function EnkPrimaryButton(props: IEnkButtonProps) {
  return (
    <button
      type={props.type || "button"}
      className={classNames(
        props.fullWidth ? "flex w-full justify-center leading-6" : "ml-6",
        "rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      )}
      onClick={props.onClick}
      data-autofocus={true}
    >
      {props.text}
    </button>
  )
}

export function EnkButtonMenuItem(props: { text: string; onClick: () => void }) {
  return (
    <MenuItem>
      <div
        key={props.text}
        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
        onClick={props.onClick}
      >
        {props.text}
      </div>
    </MenuItem>
  )
}
export function EnkMenuButton(props: IEnkMenuButtonProps) {
  return (
    <Menu as="div" className="relative">
      <MenuButton
        type="button"
        className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        onClick={props.onClick}
      >
        {props.text}
        <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
      </MenuButton>

      <MenuItems
        transition
        className="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        {props.items.map((item) => {
          return <EnkButtonMenuItem text={item} onClick={() => props.onItemSelected(item)} />
        })}
      </MenuItems>
    </Menu>
  )
}

export function EnkDownloadButton(props: IEnkButtonProps) {
  return (
    <button
      type={props.type || "button"}
      className={classNames(
        //flex items-center border p-2 mr-4
        props.fullWidth ? "flex w-full items-center justify-center leading-6" : "flex items-center p-2 mr-4 gap-x-2",
        "rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
      )}
      onClick={props.onClick}
    >
      <ArrowDownTrayIcon aria-hidden="true" className="size-5 shrink-0 text-gray-400 group-hover:text-gray-500" />
      {props.text}
    </button>
  )
}

export function EnkMoreMenuButton(props: IEnkMoreMenuButtonProps) {
  return (
    <Menu as="div" className="relative ml-6 md:hidden">
      <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
        <span className="sr-only">Open menu</span>
        <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
      </MenuButton>

      <MenuItems
        transition
        className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        {props.items.map((item, index) => {
          if (typeof item === "string") {
            return <EnkButtonMenuItem text={item} onClick={() => props.onItemSelected(item)} />
          } else {
            return (
              <div key={index} className="py-1">
                {item.map((subItem) => {
                  return <EnkButtonMenuItem text={subItem} onClick={() => props.onItemSelected(subItem)} />
                })}
              </div>
            )
          }
        })}
      </MenuItems>
    </Menu>
  )
}

export function EnkNavButtons(props: IEnkNavButtonsProps) {
  return (
    <div className="relative flex items-center rounded-md bg-white shadow-sm md:items-stretch">
      <button
        type="button"
        className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
        onClick={props.onPrev}
      >
        <span className="sr-only">Vorige {props.kind}</span>
        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
        onClick={props.onMiddle}
      >
        {props.middleText}
      </button>
      <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
      <button
        type="button"
        className="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
        onClick={props.onNext}
      >
        <span className="sr-only">Volgende {props.kind}</span>
        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  )
}

export function EnkButtonBar(props: { children: React.ReactNode }) {
  return <div className="sticky bottom-0 bg-white mt-6 mb-6 flex items-center justify-end gap-x-6 p-4">{props.children}</div>
}
